// src/services/preferencesService.js

import { repo } from "../dbservice/repo";
import { PreferenceModel } from "../models/PreferenceModel";

export const SYSTEM_PREFERENCE_TYPES = Object.freeze([
  "__system__",
  "Asset Id",
]);

export const OWNERSHIP_PREFERENCE_TYPE = "Asset Ownership";
export const OWNERSHIP_PREFERENCE_NAME = "Ownership of Assets";
export const OWNERSHIP_PREFERENCE_ID = "ownership-of-assets";

// ── Approver preference (mirrors ownership) ─────────────
export const APPROVER_PREFERENCE_TYPE = "Approver";
export const APPROVER_PREFERENCE_NAME = "Approver Workflow";
export const APPROVER_PREFERENCE_ID   = "asset-approver";

// ── Public QR preference ────────────────────────────────
export const PUBLIC_QR_PREFERENCE_TYPE = "Public QR";
export const PUBLIC_QR_PREFERENCE_NAME = "Public QR Access";
export const PUBLIC_QR_PREFERENCE_ID   = "public-qr-access";

// ── Allow duplicate open tickets per asset ──────────────
export const DUPLICATE_TICKET_PREFERENCE_TYPE = "Duplicate Tickets";
export const DUPLICATE_TICKET_PREFERENCE_NAME = "Allow Multiple Open Tickets per Asset";
export const DUPLICATE_TICKET_PREFERENCE_ID   = "allow-duplicate-open-tickets";
class PreferencesService {
  constructor() {
    this.collectionName = "preferences";
  }

  _ensureString(value) {
    if (value === null || value === undefined) return "";
    if (typeof value === "string") return value;
    if (typeof value === "number" || typeof value === "boolean")
      return String(value);
    if (typeof value === "object") {
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }
    return String(value);
  }

  _excludeDeleted(items = []) {
    return (items || []).filter((item) => item && item.deleted !== true);
  }

  _excludeSystem(items = []) {
    return (items || []).filter(
      (item) => item && !SYSTEM_PREFERENCE_TYPES.includes(item.preferenceType)
    );
  }

  _sortByName(items = []) {
    return items.sort((a, b) =>
      String(a.preferenceName || "").localeCompare(
        String(b.preferenceName || "")
      )
    );
  }

  _toPayload(item) {
    if (!item || typeof item !== "object") return {};
    const sanitized = {
      ...item,
      preferenceValue: this._ensureString(item.preferenceValue),
    };
    return new PreferenceModel(sanitized).toPayload();
  }

  _isAuthorized(currentUser) {
    return Boolean(currentUser?.uid || currentUser?.userId || currentUser?.id);
  }

  _canManage(currentUser) {
    const role = currentUser?.role
      ? String(currentUser.role).toLowerCase()
      : "";
    return role === "admin" || role === "superadmin";
  }

  // ── READ METHODS ────────────────────────────────────────

  async getAll(currentUser, limitVal = 500) {
    if (!this._isAuthorized(currentUser) || !currentUser.companyId) return [];
    try {
      const raw = await repo.fetchData(
        this.collectionName,
        [{ field: "companyId", value: currentUser.companyId }],
        [],
        limitVal
      );
      const activeUserPreferences = this._excludeSystem(
        this._excludeDeleted(raw)
      );
      const payloads = activeUserPreferences.map((item) =>
        this._toPayload(item)
      );
      return this._sortByName(payloads);
    } catch (err) {
      console.error("preferencesService.getAll failed:", err);
      return [];
    }
  }

  async getAllIncludingSystem(currentUser, limitVal = 500) {
    if (!this._isAuthorized(currentUser) || !currentUser.companyId) return [];
    try {
      const raw = await repo.fetchData(
        this.collectionName,
        [{ field: "companyId", value: currentUser.companyId }],
        [],
        limitVal
      );
      const activePreferences = this._excludeDeleted(raw);
      const payloads = activePreferences.map((item) => this._toPayload(item));
      return this._sortByName(payloads);
    } catch (err) {
      console.error("preferencesService.getAllIncludingSystem failed:", err);
      return [];
    }
  }

  async getByType(currentUser, preferenceType) {
    if (!preferenceType) return [];
    if (!this._isAuthorized(currentUser) || !currentUser.companyId) return [];
    try {
      const raw = await repo.fetchData(
        this.collectionName,
        [
          { field: "preferenceType", value: preferenceType },
          { field: "companyId", value: currentUser.companyId },
        ],
        [],
        200
      );
      const active = this._excludeDeleted(raw);
      const payloads = active.map((item) => this._toPayload(item));
      return this._sortByName(payloads);
    } catch (err) {
      console.error("preferencesService.getByType failed:", err);
      return [];
    }
  }

  async getById(currentUser, id) {
    if (!id || !this._isAuthorized(currentUser) || !currentUser.companyId)
      return null;
    try {
      const filters = [{ field: "id", value: id }];
      const raw = await repo.fetchData(this.collectionName, filters, [], 1);
      const row = raw?.[0];
      if (row && row.deleted !== true) {
        if (row.companyId === currentUser.companyId) {
          return this._toPayload(row);
        }
      }
    } catch (err) {
      console.error("preferencesService.getById failed:", err);
    }
    return null;
  }

  async getByPreferenceId(currentUser, preferenceId) {
    if (
      !preferenceId ||
      !this._isAuthorized(currentUser) ||
      !currentUser.companyId
    ) {
      return null;
    }
    try {
      const raw = await repo.fetchData(
        this.collectionName,
        [
          { field: "preferenceId", value: preferenceId },
          { field: "companyId", value: currentUser.companyId },
        ],
        [],
        20
      );
      const live = (raw || []).filter((r) => r && r.deleted !== true);
      if (live.length === 0) return null;
      if (live.length > 1) {
        console.warn(
          `[preferencesService] Found ${live.length} rows for preferenceId="${preferenceId}". Returning the first.`
        );
      }
      return this._toPayload(live[0]);
    } catch (err) {
      console.error("preferencesService.getByPreferenceId failed:", err);
      return null;
    }
  }

  async getAvailableTypes(currentUser) {
    const all = await this.getAll(currentUser);
    const types = new Set(
      all
        .map((p) => p.preferenceType)
        .filter((t) => Boolean(t) && !SYSTEM_PREFERENCE_TYPES.includes(t))
    );
    return Array.from(types).sort((a, b) => a.localeCompare(b));
  }

  // ── OWNERSHIP HELPERS ───────────────────────────────────

  async getOwnershipPreference(currentUser) {
    if (!currentUser?.companyId) return null;

    try {
      const raw = await repo.fetchData(
        this.collectionName,
        [
          { field: "companyId", value: currentUser.companyId },
          { field: "preferenceType", value: OWNERSHIP_PREFERENCE_TYPE },
        ],
        [],
        50
      );
      const live = (raw || []).filter((r) => r && r.deleted !== true);

      if (live.length > 1) {
        const [, ...dupes] = live;
        for (const dup of dupes) {
          await repo.upsertData(this.collectionName, dup.id, {
            ...dup,
            deleted: true,
          });
        }
      }
    } catch (err) {
      console.warn("Ownership dedupe skipped:", err);
    }

    return await this.getByPreferenceId(
      currentUser,
      OWNERSHIP_PREFERENCE_ID
    );
  }

  async isOwnershipEnabled(currentUser) {
    const pref = await this.getOwnershipPreference(currentUser);
    if (!pref) return false;
    return String(pref.preferenceValue).trim().toLowerCase() === "yes";
  }

  async setOwnershipPreference(currentUser, value) {
    const normalized =
      typeof value === "boolean"
        ? value
          ? "yes"
          : "no"
        : String(value).trim().toLowerCase() === "yes"
        ? "yes"
        : "no";

    const existing = await this.getOwnershipPreference(currentUser);

    const data = {
      preferenceId: OWNERSHIP_PREFERENCE_ID,
      preferenceType: OWNERSHIP_PREFERENCE_TYPE,
      preferenceName: OWNERSHIP_PREFERENCE_NAME,
      preferenceValue: normalized,
      deleted: false,
      scope: "company",
    };

    if (existing?.id) {
      return await this.update(currentUser, existing.id, data);
    }
    return await this.create(currentUser, data);
  }

  // ── APPROVER HELPERS ────────────────────────────────────

  async getApproverPreference(currentUser) {
    if (!currentUser?.companyId) return null;

    try {
      const raw = await repo.fetchData(
        this.collectionName,
        [
          { field: "companyId", value: currentUser.companyId },
          { field: "preferenceType", value: APPROVER_PREFERENCE_TYPE },
        ],
        [],
        50
      );
      const live = (raw || []).filter((r) => r && r.deleted !== true);

      if (live.length > 1) {
        const [, ...dupes] = live;
        for (const dup of dupes) {
          await repo.upsertData(this.collectionName, dup.id, {
            ...dup,
            deleted: true,
          });
        }
      }
    } catch (err) {
      console.warn("Approver dedupe skipped:", err);
    }

    return await this.getByPreferenceId(currentUser, APPROVER_PREFERENCE_ID);
  }

  async isApproverEnabled(currentUser) {
    const pref = await this.getApproverPreference(currentUser);
    if (!pref) return false;
    return String(pref.preferenceValue).trim().toLowerCase() === "yes";
  }

  async setApproverPreference(currentUser, value) {
    const normalized =
      typeof value === "boolean"
        ? value
          ? "yes"
          : "no"
        : String(value).trim().toLowerCase() === "yes"
        ? "yes"
        : "no";

    const existing = await this.getApproverPreference(currentUser);

    const data = {
      preferenceId: APPROVER_PREFERENCE_ID,
      preferenceType: APPROVER_PREFERENCE_TYPE,
      preferenceName: APPROVER_PREFERENCE_NAME,
      preferenceValue: normalized,
      deleted: false,
      scope: "company",
    };

    if (existing?.id) {
      return await this.update(currentUser, existing.id, data);
    }
    return await this.create(currentUser, data);
  }

  // ── PUBLIC QR HELPERS (anonymous-safe) ──────────────────
 

  async getPublicQrPreference(currentUserOrCompanyId) {
    const companyId =
      typeof currentUserOrCompanyId === "string"
        ? currentUserOrCompanyId
        : currentUserOrCompanyId?.companyId;

    if (!companyId) return null;

 
    try {
      const raw = await repo.fetchData(
        this.collectionName,
        [
          { field: "companyId", value: companyId },
          { field: "preferenceType", value: PUBLIC_QR_PREFERENCE_TYPE },
        ],
        [],
        50
      );
      const live = (raw || []).filter((r) => r && r.deleted !== true);

      if (live.length === 0) return null;

    
      const exact = live.find(
        (r) => r.preferenceId === PUBLIC_QR_PREFERENCE_ID
      );
      const chosen = exact || live[0];

      if (live.length > 1) {
        for (const dup of live) {
          if (dup.id === chosen.id) continue;
          try {
            await repo.upsertData(this.collectionName, dup.id, {
              ...dup,
              deleted: true,
            });
          } catch {
            /* non-blocking */
          }
        }
      }

      return this._toPayload(chosen);
    } catch (err) {
      console.warn("PublicQR pref read failed:", err);
      return null;
    }
  }

  async isPublicQrEnabled(currentUserOrCompanyId) {
    const pref = await this.getPublicQrPreference(currentUserOrCompanyId);
    if (!pref) return false;
    return String(pref.preferenceValue).trim().toLowerCase() === "yes";
  }

  async setPublicQrPreference(currentUser, value) {
    const normalized =
      typeof value === "boolean"
        ? value
          ? "yes"
          : "no"
        : String(value).trim().toLowerCase() === "yes"
        ? "yes"
        : "no";

    const existing = await this.getPublicQrPreference(currentUser);

    const data = {
      preferenceId: PUBLIC_QR_PREFERENCE_ID,
      preferenceType: PUBLIC_QR_PREFERENCE_TYPE,
      preferenceName: PUBLIC_QR_PREFERENCE_NAME,
      preferenceValue: normalized,
      deleted: false,
      scope: "company",
    };

       if (existing?.id) {
      return await this.update(currentUser, existing.id, data);
    }
    return await this.create(currentUser, data);
  }

  // ── DUPLICATE TICKET HELPERS ────────────────────────────

  async getDuplicateTicketPreference(currentUser) {
    if (!currentUser?.companyId) return null;

    try {
      const raw = await repo.fetchData(
        this.collectionName,
        [
          { field: "companyId", value: currentUser.companyId },
          { field: "preferenceType", value: DUPLICATE_TICKET_PREFERENCE_TYPE },
        ],
        [],
        50
      );
      const live = (raw || []).filter((r) => r && r.deleted !== true);

      if (live.length > 1) {
        const [, ...dupes] = live;
        for (const dup of dupes) {
          await repo.upsertData(this.collectionName, dup.id, {
            ...dup,
            deleted: true,
          });
        }
      }
    } catch (err) {
      console.warn("DuplicateTicket dedupe skipped:", err);
    }

    return await this.getByPreferenceId(currentUser, DUPLICATE_TICKET_PREFERENCE_ID);
  }

  async isDuplicateTicketAllowed(currentUser) {
    const pref = await this.getDuplicateTicketPreference(currentUser);
    if (!pref) return false; // default: NOT allowed
    return String(pref.preferenceValue).trim().toLowerCase() === "yes";
  }

  async setDuplicateTicketPreference(currentUser, value) {
    const normalized =
      typeof value === "boolean"
        ? value
          ? "yes"
          : "no"
        : String(value).trim().toLowerCase() === "yes"
        ? "yes"
        : "no";

    const existing = await this.getDuplicateTicketPreference(currentUser);

    const data = {
      preferenceId: DUPLICATE_TICKET_PREFERENCE_ID,
      preferenceType: DUPLICATE_TICKET_PREFERENCE_TYPE,
      preferenceName: DUPLICATE_TICKET_PREFERENCE_NAME,
      preferenceValue: normalized,
      deleted: false,
      scope: "company",
    };

    if (existing?.id) {
      return await this.update(currentUser, existing.id, data);
    }
    return await this.create(currentUser, data);
  }

  // ── WRITE METHODS ───────────────────────────────────────

  async create(currentUser, data, { allowSystem = false } = {}) {
    if (!this._isAuthorized(currentUser))
      throw new Error("Unauthorized: Cannot create preference.");
    if (!this._canManage(currentUser))
      throw new Error(
        "Forbidden: Only administrators can create preferences."
      );
    if (!currentUser.companyId)
      throw new Error(
        "Cannot create preference: user has no company scope."
      );

    if (
      !allowSystem &&
      SYSTEM_PREFERENCE_TYPES.includes(data?.preferenceType)
    ) {
      throw new Error(
        `"${data.preferenceType}" is a reserved system preference type.`
      );
    }

    const sanitizedData = {
      ...data,
      preferenceValue: this._ensureString(data.preferenceValue),
      companyId: currentUser.companyId,
      userId: data.userId || currentUser.id || null,
      scope: data.scope || "company",
    };

    const model = new PreferenceModel(sanitizedData, { isCreation: true });
    if (!model.isValid()) {
      throw new Error(
        "Invalid preference data: Preference Type, Name, and Value are required."
      );
    }

    const payload = model.toPayload();
    await repo.upsertData(this.collectionName, payload.id, payload);
    return payload;
  }

  async update(currentUser, id, data, { allowSystem = false } = {}) {
    if (!this._isAuthorized(currentUser))
      throw new Error("Unauthorized: Cannot update preference.");
    if (!this._canManage(currentUser))
      throw new Error(
        "Forbidden: Only administrators can update preferences."
      );
    if (!id) throw new Error("Preference ID is required for update.");

    // Safe retrieval supporting repos with or without fetchById
    let raw = null;
    if (typeof repo.fetchById === "function") {
      try {
        raw = await repo.fetchById(this.collectionName, id);
      } catch {
        raw = null;
      }
    }
    if (!raw) {
      const rows = await repo.fetchData(
        this.collectionName,
        [{ field: "id", value: id }],
        [],
        1
      );
      raw = rows?.[0];
    }

    if (
      !raw ||
      raw.deleted === true ||
      raw.companyId !== currentUser.companyId
    ) {
      throw new Error("Preference not found or not accessible.");
    }

    if (
      !allowSystem &&
      SYSTEM_PREFERENCE_TYPES.includes(raw.preferenceType)
    ) {
      throw new Error("Direct edit of system properties is forbidden.");
    }

    const sanitizedData = {
      ...data,
      preferenceId: data.preferenceId || raw.preferenceId || null,
      preferenceType: data.preferenceType || raw.preferenceType || null,
      preferenceName: data.preferenceName || raw.preferenceName || null,
      companyId: raw.companyId,
      userId: data.userId ?? raw.userId ?? null,
      scope: data.scope || raw.scope || "company",
      preferenceValue: this._ensureString(
        data.preferenceValue ?? raw.preferenceValue
      ),
      deleted: raw.deleted ?? false,
    };

    const model = new PreferenceModel(sanitizedData);
    const payload = model.toPayload();

    delete payload.id;

    await repo.upsertData(this.collectionName, id, payload);
    return { id, ...payload };
  }

  async delete(currentUser, id) {
    if (!this._isAuthorized(currentUser))
      throw new Error("Unauthorized: Cannot delete preference.");
    if (!this._canManage(currentUser))
      throw new Error(
        "Forbidden: Only administrators can delete preferences."
      );
    if (!id) throw new Error("Preference ID is required for delete.");

    const existing = await this.getById(currentUser, id);
    if (!existing) throw new Error("Preference not found or not accessible.");
    if (SYSTEM_PREFERENCE_TYPES.includes(existing.preferenceType)) {
      throw new Error("System preferences cannot be deleted.");
    }
    return await repo.deleteData(this.collectionName, id);
  }

  async upsert(currentUser, id, data) {
    if (!this._isAuthorized(currentUser))
      throw new Error("Unauthorized: Cannot upsert preference.");
    if (!this._canManage(currentUser))
      throw new Error(
        "Forbidden: Only administrators can upsert preferences."
      );

    const sanitizedData = {
      ...data,
      preferenceValue: this._ensureString(data.preferenceValue),
    };
    return await repo.upsertData(this.collectionName, id, sanitizedData);
  }
}

export const preferencesService = new PreferencesService();
export default preferencesService;